/* Solo theme scripts */
/* ---------------------------------------------------------------- core */
(function () {
  const Solo = (window.Solo = window.Solo || {});
  Solo.routes = Solo.routes || { root: '/', cartAdd: '/cart/add', cartChange: '/cart/change', cart: '/cart' };

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  Solo.$ = $;
  Solo.$$ = $$;

  Solo.isDesktop = () => window.matchMedia('(min-width: 750px)').matches;

  Solo.debounce = (fn, wait = 150) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  Solo.emit = (name, detail) => document.dispatchEvent(new CustomEvent('solo:' + name, { detail }));
  Solo.on = (name, fn) => document.addEventListener('solo:' + name, (e) => fn(e.detail, e));

  Solo.fetchJSON = async (url, options = {}) => {
    const res = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(options.headers || {}) },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || Solo.strings?.cartError || 'Error');
      err.data = data;
      throw err;
    }
    return data;
  };

  Solo.fetchHTML = async (url) => {
    const res = await fetch(url, { credentials: 'same-origin' });
    const text = await res.text();
    return new DOMParser().parseFromString(text, 'text/html');
  };

  /* ---- money ---- */
  function groupNumber(num, decimals, thousands, decimal) {
    if (isNaN(num) || num == null) return '0';
    const fixed = (num / 100).toFixed(decimals);
    const [whole, frac] = fixed.split('.');
    const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return frac ? grouped + decimal + frac : grouped;
  }
  Solo.formatMoney = (cents, format) => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Math.round(Number(cents) || 0);
    const fmt = format || Solo.moneyFormat || '${{amount}}';
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return groupNumber(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return groupNumber(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return groupNumber(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return groupNumber(cents, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return groupNumber(cents, 0, ' ', ',');
        case 'amount_with_space_separator': return groupNumber(cents, 2, ' ', ',');
        case 'amount_with_period_and_space_separator': return groupNumber(cents, 2, ' ', '.');
        default: return groupNumber(cents, 2, ',', '.');
      }
    });
  };
  /* money without trailing zeros (e.g. €10 instead of €10,00) */
  Solo.formatMoneyShort = (cents) => {
    const full = Solo.formatMoney(cents);
    return Math.round(cents) % 100 === 0 ? full.replace(/[.,]00(?=\D*$)/, '') : full;
  };

  /* Replace price tokens inside a template string. vals: {quantity,name,price,compare_price,amount_saved,amount_saved_rounded,price_each,compare_price_each} */
  Solo.moneyText = (tpl, vals) => {
    if (!tpl) return '';
    const m = (c) => Solo.formatMoneyShort(c || 0);
    let out = tpl;
    const pct = vals.compare_price > 0 ? Math.round(((vals.compare_price - vals.price) * 100) / vals.compare_price) + '%' : '0%';
    const map = {
      '[quantity]': vals.quantity != null ? String(vals.quantity) : '',
      '[name]': vals.name || '',
      '[price_each]': m(vals.price_each),
      '[compare_price_each]': m(vals.compare_price_each),
      '[compare_price]': m(vals.compare_price),
      '[price]': m(vals.price),
      '[amount_saved_rounded]': m(vals.amount_saved_rounded),
      '[amount_saved]': m(vals.amount_saved),
      '[percentage_saved]': pct,
    };
    Object.keys(map).forEach((k) => {
      out = out.split(k).join(map[k]);
    });
    return out;
  };

  /* ---- scroll lock ---- */
  let locks = 0;
  Solo.lockScroll = () => {
    locks++;
    document.documentElement.classList.add('s-lock');
  };
  Solo.unlockScroll = () => {
    locks = Math.max(0, locks - 1);
    if (!locks) document.documentElement.classList.remove('s-lock');
  };

  /* ---- toast ---- */
  Solo.toast = (html, ms = 2600) => {
    let el = $('.s-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 's-toast';
      el.setAttribute('role', 'status');
      document.body.appendChild(el);
    }
    el.innerHTML = html;
    requestAnimationFrame(() => el.classList.add('is-shown'));
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-shown'), ms);
  };

  /* ---- modals ---- */
  Solo.openModal = (modal, opener) => {
    if (!modal) return;
    if (modal.parentElement !== document.body && !modal.dataset.keepInPlace) document.body.appendChild(modal);
    modal.classList.add('is-open');
    modal._opener = opener || document.activeElement;
    Solo.lockScroll();
    const focusable = modal.querySelector('[data-modal-close], button, a, input');
    if (focusable) setTimeout(() => focusable.focus({ preventScroll: true }), 60);
    modal.dispatchEvent(new CustomEvent('modal:open'));
  };
  Solo.closeModal = (modal) => {
    if (!modal || !modal.classList.contains('is-open')) return;
    modal.classList.remove('is-open');
    Solo.unlockScroll();
    $$('video', modal).forEach((v) => v.pause());
    $$('iframe', modal).forEach((f) => { if (f.src) { f.dataset.src = f.dataset.src || f.src; f.src = ''; } });
    if (modal._opener && modal._opener.focus) modal._opener.focus({ preventScroll: true });
    modal.dispatchEvent(new CustomEvent('modal:close'));
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-modal-open]');
    if (opener) {
      e.preventDefault();
      Solo.openModal($(opener.dataset.modalOpen), opener);
      return;
    }
    const closer = e.target.closest('[data-modal-close]');
    if (closer) {
      const modal = closer.closest('.s-modal, [data-modal]');
      Solo.closeModal(modal);
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    $$('.s-modal.is-open').forEach(Solo.closeModal);
    Solo.emit('escape');
  });

  /* ---- smooth scroll links ---- */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-scroll-link]');
    if (!link) return;
    const href = link.getAttribute('href') || '';
    if (!href.startsWith('#') && !href.startsWith('.')) return;
    let target = null;
    try { target = document.querySelector(href); } catch (_) { target = null; }
    if (!target) return;
    e.preventDefault();
    const headerH = Solo.headerOffset ? Solo.headerOffset() : 0;
    window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - headerH - 10, behavior: 'smooth' });
  });

  /* ---- copy helpers (section id buttons, discount codes) ---- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy]');
    if (!btn) return;
    e.preventDefault();
    const text = btn.dataset.copyText || btn.dataset.copy;
    try { await navigator.clipboard.writeText(text); } catch (_) { /* ignore */ }
    btn.setAttribute('data-done', '');
    setTimeout(() => btn.removeAttribute('data-done'), 2000);
  });

  /* ---- external links open in new tab ---- */
  document.addEventListener('DOMContentLoaded', () => {
    $$('a[href^="http"]').forEach((a) => {
      try {
        if (new URL(a.href).hostname !== location.hostname) {
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
        }
      } catch (_) { /* ignore */ }
    });
  });

  /* ---- content protection (theme setting) ---- */
  document.addEventListener('DOMContentLoaded', () => {
    if (!Solo.settings || !Solo.settings.disableInspect || (window.Shopify && Shopify.designMode)) return;
    document.addEventListener('contextmenu', (e) => e.preventDefault());
    document.addEventListener('dragstart', (e) => { if (e.target.tagName === 'IMG') e.preventDefault(); });
    document.addEventListener('keydown', (e) => {
      const k = (e.key || '').toUpperCase();
      if (e.keyCode === 123 || (e.ctrlKey && e.shiftKey && ['I', 'J', 'C'].includes(k)) || (e.ctrlKey && k === 'U') || (e.metaKey && e.altKey && ['I', 'J', 'C'].includes(k))) e.preventDefault();
    });
  });

  /* ---- section load animations ---- */
  function initAnimations(root = document, instant = false) {
    if (!Solo.settings || !Solo.settings.animations) return;
    const els = $$('.s-animate', root);
    if (!els.length) return;
    if (instant || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-shown'));
      return;
    }
    const sticky = $('.s-sticky');
    const bottom = 50 + (sticky ? sticky.offsetHeight : 0);
    const io = new IntersectionObserver(
      (entries) => entries.forEach((en) => {
        if (en.isIntersecting) en.target.classList.add('is-shown');
        else if (Solo.settings.repeatAnimations) en.target.classList.remove('is-shown');
      }),
      { rootMargin: `0px 0px -${bottom}px 0px` }
    );
    els.forEach((el) => io.observe(el));
  }
  document.addEventListener('DOMContentLoaded', () => initAnimations());
  document.addEventListener('shopify:section:load', (e) => initAnimations(e.target, true));
  Solo.initAnimations = initAnimations;
})();

/* ---------------------------------------------------------------- generic components */
(function () {
  const { $, $$ } = Solo;

  /* ===== <s-slider> : scroll-snap carousel ===== */
  class SSlider extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.track = this.querySelector('[data-track]') || this.querySelector('.s-slider__track');
      if (!this.track) return;
      this.type = this.dataset.type || 'slide';
      this.autoplay = parseFloat(this.dataset.autoplay || '0');
      this.dotsEl = this.querySelector('[data-dots]');
      this.counterEl = this.querySelector('[data-counter]');
      this.prevBtns = $$('[data-prev]', this).filter((b) => b.closest('s-slider') === this);
      this.nextBtns = $$('[data-next]', this).filter((b) => b.closest('s-slider') === this);
      this.index = 0;
      this.prevBtns.forEach((b) => b.addEventListener('click', () => this.prev()));
      this.nextBtns.forEach((b) => b.addEventListener('click', () => this.next()));
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.setupDrag();
      this.refresh();
      this._ro = new ResizeObserver(Solo.debounce(() => this.refresh(), 100));
      this._ro.observe(this);
      if (this.autoplay > 0) this.startAutoplay();
      if (this.type === 'fade') this.setActive(0);
    }
    disconnectedCallback() {
      this.stopAutoplay();
      if (this._ro) this._ro.disconnect();
    }
    get slides() {
      return Array.from(this.track.children).filter((el) => el.offsetParent !== null || this.type === 'fade');
    }
    perView() {
      const v = parseFloat(getComputedStyle(this).getPropertyValue('--per')) || 1;
      return Math.max(1, Math.floor(v + 0.001));
    }
    moveBy() {
      const d = Solo.isDesktop();
      return parseInt((d ? this.dataset.moveD : this.dataset.moveM) || this.dataset.move || '1', 10) || 1;
    }
    isStatic() {
      const d = Solo.isDesktop();
      return (d && this.dataset.staticDesktop === 'true') || (!d && this.dataset.staticMobile === 'true');
    }
    pageCount() {
      if (this.type === 'fade') return this.slides.length;
      const total = this.slides.length;
      const per = this.perView();
      return Math.max(1, Math.ceil((total - per) / this.moveBy()) + 1);
    }
    refresh() {
      const isStatic = this.isStatic();
      this.classList.toggle('is-static', isStatic);
      const pages = isStatic ? 1 : this.pageCount();
      this.pages = pages;
      this.classList.toggle('is-single', pages <= 1);
      if (this.dotsEl) {
        if (this.dotsEl.children.length !== pages) {
          this.dotsEl.innerHTML = '';
          for (let i = 0; i < pages; i++) {
            const li = document.createElement('li');
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 's-dot';
            b.setAttribute('aria-label', `${i + 1} / ${pages}`);
            if (this.dotsEl.classList.contains('s-dots--numbers')) {
              b.textContent = i + 1;
              b.classList.add('s-dot--num');
            }
            b.addEventListener('click', () => this.goTo(i));
            li.appendChild(b);
            this.dotsEl.appendChild(li);
          }
        }
        this.dotsEl.hidden = pages <= 1;
      }
      [...this.prevBtns, ...this.nextBtns].forEach((b) => (b.hidden = pages <= 1));
      this.index = Math.min(this.index, pages - 1);
      this.updateUI();
      this.adaptHeight();
    }
    slideOffset(i) {
      const s = this.slides[i];
      if (!s) return 0;
      const pad = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
      return s.offsetLeft - this.track.offsetLeft - pad - (this.track.firstElementChild ? this.track.firstElementChild.offsetLeft - this.track.offsetLeft - pad : 0);
    }
    goTo(page, smooth = true) {
      const pages = this.pages || this.pageCount();
      if (this.type === 'loop') page = (page + pages) % pages;
      page = Math.max(0, Math.min(page, pages - 1));
      this.index = page;
      if (this.type === 'fade') {
        this.setActive(page);
      } else {
        const slideIdx = Math.min(page * this.moveBy(), this.slides.length - 1);
        const s = this.slides[slideIdx];
        if (s) {
          const left = s.offsetLeft - this.slides[0].offsetLeft;
          this.track.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
        }
      }
      this.updateUI();
      this.adaptHeight();
      this.dispatchEvent(new CustomEvent('slider:change', { detail: { index: page } }));
    }
    next() {
      const pages = this.pages || this.pageCount();
      if (this.index >= pages - 1) {
        if (this.type === 'loop' || this.type === 'fade' || this._auto) this.goTo(0);
        return;
      }
      this.goTo(this.index + 1);
    }
    prev() {
      if (this.index <= 0) {
        if (this.type === 'loop' || this.type === 'fade') this.goTo((this.pages || 1) - 1);
        return;
      }
      this.goTo(this.index - 1);
    }
    setActive(i) {
      this.slides.forEach((s, n) => s.classList.toggle('is-active', n === i));
    }
    onScroll() {
      if (this.type === 'fade') return;
      cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(() => {
        const first = this.slides[0];
        if (!first) return;
        const step = (this.slides[1] ? this.slides[1].offsetLeft - first.offsetLeft : first.offsetWidth) * this.moveBy();
        const max = this.track.scrollWidth - this.track.clientWidth;
        let idx = step ? Math.round(this.track.scrollLeft / step) : 0;
        if (this.track.scrollLeft >= max - 2) idx = (this.pages || 1) - 1;
        if (idx !== this.index) {
          this.index = Math.max(0, Math.min(idx, (this.pages || 1) - 1));
          this.updateUI();
          this.adaptHeight();
          this.dispatchEvent(new CustomEvent('slider:change', { detail: { index: this.index } }));
        }
      });
    }
    updateUI() {
      const pages = this.pages || 1;
      if (this.dotsEl) $$('.s-dot', this.dotsEl).forEach((d, i) => d.classList.toggle('is-active', i === this.index));
      if (this.counterEl) this.counterEl.textContent = `${this.index + 1} / ${pages}`;
      if (this.type === 'slide') {
        this.prevBtns.forEach((b) => (b.disabled = this.index <= 0));
        this.nextBtns.forEach((b) => (b.disabled = this.index >= pages - 1));
      }
      this.slides.forEach((s, i) => {
        const per = this.perView();
        const start = this.index * this.moveBy();
        const vis = i >= start && i < start + per;
        s.toggleAttribute('data-visible', vis);
        if (!vis) $$('video', s).forEach((v) => { if (!v.muted) v.pause(); });
      });
    }
    adaptHeight() {
      if (this.dataset.adaptive !== 'true') return;
      const s = this.slides[this.index * this.moveBy()];
      if (!s) return;
      const inner = s.firstElementChild || s;
      this.track.style.height = inner.offsetHeight + 'px';
    }
    startAutoplay() {
      this.stopAutoplay();
      this._auto = setInterval(() => {
        if (this._hover || document.hidden) return;
        this.next();
      }, this.autoplay * 1000);
      this.addEventListener('mouseenter', () => (this._hover = true));
      this.addEventListener('mouseleave', () => (this._hover = false));
      this.addEventListener('touchstart', () => (this._hover = true), { passive: true });
      this.addEventListener('touchend', () => setTimeout(() => (this._hover = false), 3000), { passive: true });
    }
    stopAutoplay() {
      if (this._auto) clearInterval(this._auto);
      this._auto = null;
    }
    setupDrag() {
      if (this.dataset.drag === 'disabled') {
        this.track.style.overflowX = 'hidden';
        return;
      }
      let down = false, startX = 0, startLeft = 0, moved = false;
      this.track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0 || this.type === 'fade') return;
        down = true; moved = false; startX = e.clientX; startLeft = this.track.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 5 && !moved) { moved = true; this.classList.add('is-dragging'); }
        if (moved) this.track.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        if (moved) {
          this.classList.remove('is-dragging');
          if (this.dataset.drag !== 'free') requestAnimationFrame(() => this.goTo(this.nearestPage(), true));
          const block = (ev) => { ev.preventDefault(); ev.stopPropagation(); this.track.removeEventListener('click', block, true); };
          this.track.addEventListener('click', block, true);
        }
      });
    }
    nearestPage() {
      const first = this.slides[0];
      if (!first) return 0;
      const step = (this.slides[1] ? this.slides[1].offsetLeft - first.offsetLeft : first.offsetWidth) * this.moveBy();
      return Math.max(0, Math.min(Math.round(this.track.scrollLeft / (step || 1)), (this.pages || 1) - 1));
    }
  }
  customElements.define('s-slider', SSlider);

  /* ===== <s-qty> quantity input ===== */
  class SQty extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('[data-qty-minus],[data-qty-plus]').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const step = parseInt(this.input.step, 10) || 1;
          const min = parseInt(this.input.min, 10) || 0;
          const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
          let v = parseInt(this.input.value, 10) || min;
          v = btn.hasAttribute('data-qty-plus') ? v + step : v - step;
          v = Math.max(min, Math.min(max, v));
          if (String(v) !== this.input.value) {
            this.input.value = v;
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          this.updateButtons();
        })
      );
      this.input.addEventListener('change', () => this.updateButtons());
      this.updateButtons();
    }
    updateButtons() {
      const min = parseInt(this.input.min, 10) || 0;
      const v = parseInt(this.input.value, 10) || 0;
      const minus = this.querySelector('[data-qty-minus]');
      if (minus) minus.classList.toggle('is-disabled', v <= min);
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      const plus = this.querySelector('[data-qty-plus]');
      if (plus) plus.classList.toggle('is-disabled', v >= max);
    }
  }
  customElements.define('s-qty', SQty);

  /* ===== <s-video> native video ===== */
  class SVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.playBtn = this.querySelector('[data-video-play]');
      this.soundBtn = this.querySelector('[data-video-sound]');
      this.timeline = this.querySelector('[data-video-timeline] span');
      const autoplay = this.dataset.autoplay === 'true';
      if (autoplay) {
        const load = () => {
          $$('source[data-src]', this.video).forEach((s) => { s.src = s.dataset.src; s.removeAttribute('data-src'); });
          this.video.load();
          this.video.muted = true;
          this.video.play().catch(() => {});
        };
        if ('IntersectionObserver' in window) {
          const io = new IntersectionObserver((ents) => {
            ents.forEach((en) => {
              if (en.isIntersecting) {
                if (this.video.querySelector('source[data-src]')) load();
                else if (this.classList.contains('is-playing')) this.video.play().catch(() => {});
              } else if (!this.video.paused) {
                this.video.pause();
              }
            });
          }, { rootMargin: '200px' });
          io.observe(this);
        } else load();
      }
      const toggle = (e) => {
        e && e.preventDefault();
        if (this.video.paused) this.play();
        else this.pause();
      };
      if (this.playBtn) this.playBtn.addEventListener('click', toggle);
      if (this.dataset.clickToggle === 'true') this.video.addEventListener('click', toggle);
      if (this.soundBtn) this.soundBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.video.muted = !this.video.muted;
        this.classList.toggle('is-muted', this.video.muted);
      });
      this.video.addEventListener('play', () => this.classList.add('is-playing'));
      this.video.addEventListener('pause', () => this.classList.remove('is-playing'));
      if (this.timeline) this.video.addEventListener('timeupdate', () => {
        if (this.video.duration) this.timeline.style.width = (this.video.currentTime / this.video.duration) * 100 + '%';
      });
    }
    play() {
      $$('source[data-src]', this.video).forEach((s) => { s.src = s.dataset.src; s.removeAttribute('data-src'); });
      if (this.video.readyState === 0 && this.video.querySelector('source[src]') && !this.video.currentSrc) this.video.load();
      if (this.dataset.autoplay !== 'true') this.video.muted = false;
      this.video.play().catch(() => {});
    }
    pause() { this.video.pause(); }
  }
  customElements.define('s-video', SVideo);

  /* ===== <s-embed> lazy YouTube/Vimeo ===== */
  class SEmbed extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('[data-embed-play]');
      if (btn) btn.addEventListener('click', () => this.load(true));
      if (this.dataset.autoplay === 'true') {
        const io = new IntersectionObserver((ents) => ents.forEach((en) => { if (en.isIntersecting) { this.load(false); io.disconnect(); } }));
        io.observe(this);
      }
    }
    load(play) {
      if (this.querySelector('iframe')) return;
      const f = document.createElement('iframe');
      let src = this.dataset.src;
      if (play && !/autoplay=1/.test(src)) src += (src.includes('?') ? '&' : '?') + 'autoplay=1';
      f.src = src;
      f.allow = 'autoplay; encrypted-media; picture-in-picture';
      f.allowFullscreen = true;
      f.title = 'Video';
      this.appendChild(f);
    }
  }
  customElements.define('s-embed', SEmbed);

  /* ===== <s-dates> dynamic delivery dates ===== */
  class SDates extends HTMLElement {
    connectedCallback() {
      const days = (this.dataset.days || '').split(',').map((s) => s.trim());
      const months = (this.dataset.months || '').split(',').map((s) => s.trim());
      const fmt = this.dataset.format || 'mm_dd';
      const ordinal = (n) => {
        const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
      };
      const format = (d) => {
        const day = days[(d.getDay() + 6) % 7] || '';
        const mon = months[d.getMonth()] || '';
        const dd = d.getDate();
        const mm = d.getMonth() + 1;
        switch (fmt) {
          case 'day_mm_dd': return `${day}, ${mon} ${ordinal(dd)}`;
          case 'day_dd_mm': return `${day}, ${dd}. ${mon}`;
          case 'dd_mm': return `${dd}. ${mon}`;
          case 'dd_mm_no_dot': return `${dd} ${mon}`;
          case 'day_dd_mm_numeric': return `${day}, ${String(dd).padStart(2, '0')}. ${String(mm).padStart(2, '0')}.`;
          case 'dd_mm_numeric': return `${String(dd).padStart(2, '0')}. ${String(mm).padStart(2, '0')}.`;
          default: return `${mon} ${ordinal(dd)}`;
        }
      };
      const add = (n) => {
        const d = new Date();
        d.setDate(d.getDate() + (parseInt(n, 10) || 0));
        return d;
      };
      $$('[data-date-text]', this).forEach((el) => {
        const tpl = el.dataset.dateText;
        if (!tpl) return;
        el.innerHTML = tpl.split('[start_date]').join(format(add(el.dataset.min))).split('[end_date]').join(format(add(el.dataset.max)));
      });
    }
  }
  customElements.define('s-dates', SDates);

  /* ===== <s-share> ===== */
  class SShare extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('[data-share-btn]');
      const fb = this.querySelector('[data-share-fallback]');
      const copy = this.querySelector('[data-share-copy]');
      const msg = this.querySelector('[data-share-msg]');
      btn.addEventListener('click', async () => {
        if (navigator.share) {
          try { await navigator.share({ url: this.dataset.url, title: this.dataset.title }); } catch (_) { /* cancelled */ }
          return;
        }
        fb.hidden = !fb.hidden;
      });
      if (copy) copy.addEventListener('click', async () => {
        try { await navigator.clipboard.writeText(this.dataset.url); msg.textContent = Solo.strings.shareSuccess || 'Link copied'; } catch (_) { /* ignore */ }
      });
    }
  }
  customElements.define('s-share', SShare);

  /* ===== <s-discount> click-to-apply discount code ===== */
  class SDiscount extends HTMLElement {
    connectedCallback() {
      const code = this.dataset.code;
      const key = 'solo-discount-' + code;
      try { if (localStorage.getItem(key)) this.dataset.applied = 'true'; } catch (_) { /* ignore */ }
      this.querySelector('[data-discount-btn]').addEventListener('click', async () => {
        if (this.dataset.applied === 'true' || this.dataset.loading === 'true') return;
        this.dataset.loading = 'true';
        const err = this.querySelector('[data-discount-error]');
        try {
          await fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' });
          this.dataset.applied = 'true';
          try { localStorage.setItem(key, '1'); } catch (_) { /* ignore */ }
          if (err) err.hidden = true;
          Solo.emit('cart:refresh');
        } catch (_) {
          if (err) err.hidden = false;
        }
        this.dataset.loading = 'false';
      });
    }
  }
  customElements.define('s-discount', SDiscount);

  /* ===== <s-recs> product recommendations loader ===== */
  class SRecs extends HTMLElement {
    connectedCallback() {
      const io = new IntersectionObserver((ents) => {
        if (!ents.some((e) => e.isIntersecting)) return;
        io.disconnect();
        this.load();
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
    async load() {
      try {
        const doc = await Solo.fetchHTML(this.dataset.url);
        const sel = this.dataset.target;
        const fresh = sel ? doc.querySelector(sel) : doc.querySelector('s-recs');
        const target = sel ? this.querySelector(sel) || document.querySelector(sel) : this;
        if (fresh && target && fresh.innerHTML.trim()) {
          target.innerHTML = fresh.innerHTML;
          this.classList.add('is-loaded');
          Solo.emit('content:loaded', { root: target });
        } else if (this.dataset.hideEmpty !== 'false') {
          this.hidden = true;
        }
      } catch (_) {
        this.hidden = true;
      }
    }
  }
  customElements.define('s-recs', SRecs);

  /* recommendation-based upsell containers */
  document.addEventListener('DOMContentLoaded', () => {
    $$('[data-rec-url]').forEach(async (el) => {
      try {
        const doc = await Solo.fetchHTML(el.dataset.recUrl);
        const fresh = doc.querySelector(el.dataset.recTarget);
        if (fresh) {
          el.innerHTML = fresh.innerHTML;
          Solo.emit('content:loaded', { root: el });
        }
      } catch (_) { /* ignore */ }
    });
  });
})();

/* ---------------------------------------------------------------- cart */
(function () {
  const { $, $$ } = Solo;
  const SECTION = 'cart-drawer';

  const cart = (Solo.cart = {
    busy: false,
    data: null,

    sectionsPayload() {
      return { sections: [SECTION], sections_url: window.location.pathname };
    },

    async get() {
      this.data = await Solo.fetchJSON('/cart.js', { method: 'GET', headers: {} });
      return this.data;
    },

    async add(items, opts = {}) {
      const body = { items, ...this.sectionsPayload() };
      const res = await Solo.fetchJSON('/cart/add.js', { method: 'POST', body: JSON.stringify(body) });
      await this.afterChange(res.sections, opts);
      Solo.emit('cart:added', { items: res.items || items });
      return res;
    },

    async change(key, quantity, opts = {}) {
      const body = { id: key, quantity, ...this.sectionsPayload() };
      const res = await Solo.fetchJSON('/cart/change.js', { method: 'POST', body: JSON.stringify(body) });
      this.data = res;
      await this.afterChange(res.sections, { open: false, ...opts });
      return res;
    },

    async update(updates, opts = {}) {
      const body = { updates, ...this.sectionsPayload() };
      const res = await Solo.fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify(body) });
      this.data = res;
      await this.afterChange(res.sections, { open: false, ...opts });
      return res;
    },

    async refresh(opts = {}) {
      const res = await fetch(`${window.location.pathname}?sections=${SECTION}`, { credentials: 'same-origin' });
      const json = await res.json();
      await this.afterChange(json, { open: false, ...opts });
    },

    async afterChange(sections, opts = {}) {
      if (sections && sections[SECTION]) this.renderDrawer(sections[SECTION]);
      try { await this.get(); } catch (_) { /* ignore */ }
      this.updateCounts();
      Solo.emit('cart:updated', { cart: this.data });
      if (opts.open !== false) {
        if (Solo.cartType === 'drawer' && this.drawer) this.drawer.open();
        else if (Solo.cartType === 'page') window.location.href = Solo.routes.cart;
        else this.notify();
      }
      if (!opts.skipSync) this.sync();
    },

    renderDrawer(html) {
      const drawer = this.drawer || $('s-cart-drawer');
      if (!drawer) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('s-cart-drawer');
      if (!fresh) return;
      const scrollTop = $('.s-drawer__body', drawer) ? $('.s-drawer__body', drawer).scrollTop : 0;
      $('[data-cart-content]', drawer).innerHTML = $('[data-cart-content]', fresh).innerHTML;
      drawer.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
      drawer.dataset.count = fresh.dataset.count;
      drawer.dataset.totalCount = fresh.dataset.totalCount;
      const body = $('.s-drawer__body', drawer);
      if (body) body.scrollTop = scrollTop;
      Solo.emit('content:loaded', { root: drawer });
    },

    updateCounts() {
      const count = this.drawer ? parseInt(this.drawer.dataset.count, 10) : this.data ? this.data.item_count : 0;
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = !count;
      });
      $$('[data-cart-count-wrap]').forEach((el) => el.classList.toggle('has-items', count > 0));
    },

    notify() {
      const html = `${Solo.strings.addedToCart || 'Added to cart'} &nbsp;<a href="${Solo.routes.cart}" style="text-decoration:underline">${Solo.strings.viewCart || 'View cart'}</a>`;
      Solo.toast(html, 4000);
    },

    /* keep gift products in sync with the cart contents */
    async sync() {
      if (this._syncing || !this.data) return;
      const ops = {};
      const lines = this.data.items;
      const qtyOfVariant = (id) => lines.filter((l) => String(l.variant_id) === String(id)).reduce((s, l) => s + l.quantity, 0);
      const keyOf = (id) => (lines.find((l) => String(l.variant_id) === String(id)) || {}).key;

      // cart drawer gift blocks
      if (this.drawer) {
        $$('s-cart-gift', this.drawer).forEach((g) => {
          const id = g.dataset.variant;
          const inCart = qtyOfVariant(id);
          if (g.dataset.unlocked === 'true' && !inCart) ops[id] = 1;
          if (g.dataset.unlocked !== 'true' && inCart) ops[keyOf(id)] = 0;
          if (g.dataset.unlocked === 'true' && inCart > 1) ops[keyOf(id)] = 1;
        });
      }
      // product page quantity gifts (rules stored by s-gifts)
      const rules = Solo.giftRules || [];
      rules.forEach((rule) => {
        const mainQty = lines.filter((l) => l.handle === rule.mainHandle).reduce((s, l) => s + l.quantity, 0);
        rule.gifts.forEach((gift) => {
          const inCart = qtyOfVariant(gift.variant);
          const unlocked = mainQty >= gift.qty;
          if (unlocked && !inCart) ops[gift.variant] = 1;
          if (!unlocked && inCart) ops[keyOf(gift.variant)] = 0;
        });
      });
      const addIds = Object.keys(ops).filter((k) => /^\d+$/.test(k) && ops[k] > 0 && !keyOf(k));
      const changes = Object.keys(ops).filter((k) => !addIds.includes(k));
      if (!addIds.length && !changes.length) return;
      this._syncing = true;
      try {
        if (addIds.length) await this.add(addIds.map((id) => ({ id: Number(id), quantity: 1 })), { open: false, skipSync: true });
        for (const k of changes) await this.change(k, ops[k], { skipSync: true });
      } catch (_) { /* ignore */ }
      this._syncing = false;
    },
  });

  Solo.on('cart:refresh', () => cart.refresh());

  /* ===== <s-cart-drawer> ===== */
  class SCartDrawer extends HTMLElement {
    connectedCallback() {
      cart.drawer = this;
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-cart-open]');
        if (trigger && Solo.cartType === 'drawer') {
          e.preventDefault();
          this.open(trigger);
        }
      });
      Solo.on('escape', () => this.close());
      document.addEventListener('shopify:section:select', (e) => { if (e.target.id === 'shopify-section-cart-drawer') this.open(); });
      document.addEventListener('shopify:section:deselect', (e) => { if (e.target.id === 'shopify-section-cart-drawer') this.close(); });
      cart.get().then(() => { cart.updateCounts(); cart.sync(); }).catch(() => {});
    }
    open(opener) {
      if (this.classList.contains('is-open')) return;
      this._opener = opener || document.activeElement;
      this.classList.add('is-open');
      Solo.lockScroll();
      const panel = $('.s-drawer__panel', this);
      setTimeout(() => panel && panel.focus({ preventScroll: true }), 50);
      Solo.emit('drawer:open');
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      Solo.unlockScroll();
      if (this._opener && this._opener.focus) this._opener.focus({ preventScroll: true });
    }
    lineOf(el) {
      return el.closest('.s-line');
    }
    async setLine(line, qty) {
      line.classList.add('is-loading');
      const err = $('[data-line-error]', line);
      try {
        await cart.change(line.dataset.key, qty);
      } catch (e) {
        line.classList.remove('is-loading');
        if (err) { err.textContent = e.message; err.hidden = false; }
        const input = $('[data-line-input]', line);
        if (input) input.value = line.dataset.qty;
      }
    }
    onClick(e) {
      if (e.target.closest('[data-drawer-close]')) {
        e.preventDefault();
        this.close();
        return;
      }
      const minus = e.target.closest('[data-line-minus]');
      const plus = e.target.closest('[data-line-plus]');
      const remove = e.target.closest('[data-line-remove]');
      if (minus || plus || remove) {
        e.preventDefault();
        const line = this.lineOf(e.target);
        const input = $('[data-line-input]', line);
        const step = parseInt(input.step, 10) || 1;
        let q = parseInt(line.dataset.qty, 10) || 0;
        if (remove) q = 0;
        else q = Math.max(0, q + (plus ? step : -step));
        if (input) input.value = q;
        this.setLine(line, q);
        return;
      }
      const checkout = e.target.closest('[data-checkout-btn]');
      if (checkout) {
        const tnc = $('s-tnc', this);
        if (tnc && !tnc.isChecked()) {
          e.preventDefault();
          tnc.warn();
          return;
        }
        e.preventDefault();
        checkout.classList.add('is-loading');
        window.location.href = '/checkout';
      }
    }
    onChange(e) {
      const input = e.target.closest('[data-line-input]');
      if (input) {
        const line = this.lineOf(input);
        const q = Math.max(0, parseInt(input.value, 10) || 0);
        if (String(q) !== line.dataset.qty) this.setLine(line, q);
        return;
      }
      const note = e.target.closest('[data-cart-note]');
      if (note) {
        fetch('/cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
      }
    }
  }
  customElements.define('s-cart-drawer', SCartDrawer);

  /* ===== countdown timer (persists for the session) ===== */
  class STimer extends HTMLElement {
    connectedCallback() {
      const dur = parseInt(this.dataset.duration, 10) || 300;
      const key = 'solo-cart-timer';
      let end;
      try { end = parseInt(sessionStorage.getItem(key), 10); } catch (_) { end = 0; }
      if (!end || end < Date.now()) {
        end = Date.now() + dur * 1000;
        try { sessionStorage.setItem(key, end); } catch (_) { /* ignore */ }
      }
      const tick = () => {
        let left = Math.max(0, Math.round((end - Date.now()) / 1000));
        if (left <= 0) {
          end = Date.now() + dur * 1000;
          try { sessionStorage.setItem(key, end); } catch (_) { /* ignore */ }
          left = dur;
        }
        this.textContent = String(Math.floor(left / 60)).padStart(2, '0') + ':' + String(left % 60).padStart(2, '0');
      };
      tick();
      this._t = setInterval(tick, 1000);
    }
    disconnectedCallback() { clearInterval(this._t); }
  }
  customElements.define('s-timer', STimer);

  /* ===== cart discount field ===== */
  class SCartDiscount extends HTMLElement {
    connectedCallback() {
      const input = $('[data-discount-input]', this);
      const btn = $('[data-discount-apply]', this);
      const msg = $('[data-discount-msg]', this);
      const apply = async () => {
        const code = (input.value || '').trim();
        if (!code) { msg.textContent = this.dataset.error; msg.hidden = false; return; }
        msg.hidden = true;
        btn.classList.add('is-loading');
        try {
          const existing = ((cart.data && cart.data.discount_codes) || []).filter((d) => d.applicable).map((d) => d.code);
          const codes = Array.from(new Set([...existing, code])).join(',');
          await Solo.fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ discount: codes }) });
          await cart.refresh();
        } catch (_) {
          try { await fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' }); await cart.refresh(); } catch (__) { /* ignore */ }
        }
        btn.classList.remove('is-loading');
      };
      btn.addEventListener('click', apply);
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); apply(); } });
      $$('[data-discount-remove]', this).forEach((b) => b.addEventListener('click', async () => {
        const rm = b.dataset.discountRemove;
        const codes = ((cart.data && cart.data.discount_codes) || []).map((d) => d.code).filter((c) => c !== rm).join(',');
        await Solo.fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ discount: codes }) }).catch(() => {});
        await cart.refresh();
      }));
    }
  }
  customElements.define('s-cart-discount', SCartDiscount);

  /* ===== terms checkbox ===== */
  class STnc extends HTMLElement {
    connectedCallback() {
      this.input = $('[data-tnc-input]', this);
      this.warning = $('[data-tnc-warning]', this);
      const drawer = this.closest('s-cart-drawer') || document;
      this.btn = $('[data-checkout-btn]', drawer);
      if (this.warning && this.btn) {
        const pos = this.dataset.warningPosition;
        if (pos === 'above-button') this.btn.parentElement.insertBefore(this.warning, this.btn);
        else if (pos === 'under-button') this.btn.insertAdjacentElement('afterend', this.warning);
      }
      const state = () => {
        if (this.btn && this.dataset.disableBtn === 'true') this.btn.classList.toggle('is-disabled-tnc', !this.input.checked);
        if (this.input.checked && this.warning) this.warning.hidden = true;
      };
      try { this.input.checked = sessionStorage.getItem('solo-tnc') === '1'; } catch (_) { /* ignore */ }
      this.input.addEventListener('change', () => {
        try { sessionStorage.setItem('solo-tnc', this.input.checked ? '1' : '0'); } catch (_) { /* ignore */ }
        state();
      });
      state();
    }
    isChecked() { return this.input.checked; }
    warn() { if (this.warning) this.warning.hidden = false; }
  }
  customElements.define('s-tnc', STnc);

  /* ===== cart gift (handled by cart.sync) ===== */
  class SCartGift extends HTMLElement {}
  customElements.define('s-cart-gift', SCartGift);
})();

/* ---------------------------------------------------------------- product gallery */
(function () {
  const { $, $$ } = Solo;

  class SGallery extends HTMLElement {
    connectedCallback() {
      this.list = $('[data-gallery-list]', this);
      this.thumbsList = $('[data-thumbs-list]', this);
      this.layout = this.dataset.layout;
      this.dots = $('[data-gallery-dots]', this);
      this.current = $('[data-gallery-current]', this);
      this.total = $('[data-gallery-total]', this);
      this.lightbox = $('[data-lightbox]', this);

      $$('[data-gallery-prev]', this).forEach((b) => b.addEventListener('click', () => this.step(-1)));
      $$('[data-gallery-next]', this).forEach((b) => b.addEventListener('click', () => this.step(1)));
      if (this.thumbsList) {
        this.thumbsList.addEventListener('click', (e) => {
          const li = e.target.closest('.s-thumbs__item');
          if (li) this.setActive(li.dataset.target, { userAction: true });
        });
        const tp = $('[data-thumbs-prev]', this), tn = $('[data-thumbs-next]', this);
        if (tp) tp.addEventListener('click', () => this.scrollThumbs(-1));
        if (tn) tn.addEventListener('click', () => this.scrollThumbs(1));
        this.thumbsList.addEventListener('scroll', Solo.debounce(() => this.updateThumbArrows(), 60), { passive: true });
      }
      this.list.addEventListener('scroll', () => this.onListScroll(), { passive: true });
      this.addEventListener('click', (e) => {
        const z = e.target.closest('[data-gallery-zoom]');
        if (z) {
          e.preventDefault();
          this.openLightbox(z.dataset.galleryZoom);
        }
      });
      this.buildDots();
      this.syncActiveUI();
      if (window.ResizeObserver) {
        this._ro = new ResizeObserver(() => {
          const active = this.activeItem();
          if (active) this.style.setProperty('--main-h', active.offsetHeight + 'px');
          this.updateThumbArrows();
        });
        this._ro.observe(this.list);
      }
      this.updateThumbArrows();
    }
    items() {
      return $$('.s-gallery__item', this.list).filter((el) => !el.classList.contains('is-filtered') && getComputedStyle(el).display !== 'none');
    }
    activeItem() {
      return $('.s-gallery__item.is-active', this.list) || this.items()[0];
    }
    isSliderMode() {
      return !Solo.isDesktop();
    }
    buildDots() {
      const items = this.items();
      if (this.total) this.total.textContent = items.length;
      if (!this.dots) return;
      this.dots.innerHTML = '';
      items.forEach((it, i) => {
        const li = document.createElement('li');
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 's-dot';
        b.setAttribute('aria-label', `${i + 1} / ${items.length}`);
        b.addEventListener('click', () => this.setActive(it.dataset.mediaId, { userAction: true }));
        li.appendChild(b);
        this.dots.appendChild(li);
      });
      this.closest('.s-gallery__main');
    }
    step(dir) {
      const items = this.items();
      const idx = items.indexOf(this.activeItem());
      const next = idx + dir;
      if (next < 0 || next >= items.length) return;
      if (items[next]) this.setActive(items[next].dataset.mediaId, { userAction: true });
    }
    setActive(mediaId, opts = {}) {
      const target = $(`.s-gallery__item[data-media-id="${mediaId}"]`, this.list);
      if (!target) return;
      if (opts.prepend && this.dataset.prepend === 'true' && (this.layout === 'stacked' || this.layout === 'columns') && Solo.isDesktop()) {
        this.list.prepend(target);
        if (this.thumbsList) {
          const t = $(`.s-thumbs__item[data-target="${mediaId}"]`, this.thumbsList);
          if (t) this.thumbsList.prepend(t);
        }
        this.buildDots();
      }
      $$('.s-gallery__item', this.list).forEach((el) => el.classList.toggle('is-active', el === target));
      this.pauseOthers(target);
      if (this.isSliderMode()) {
        this._programmatic = true;
        this.list.scrollTo({ left: target.offsetLeft - this.list.offsetLeft - (parseFloat(getComputedStyle(this.list).paddingLeft) || 0), behavior: opts.instant ? 'auto' : 'smooth' });
        clearTimeout(this._pt);
        this._pt = setTimeout(() => (this._programmatic = false), 600);
      } else if ((this.layout === 'stacked' || this.layout === 'columns') && opts.scrollIntoView) {
        const top = target.getBoundingClientRect().top + window.scrollY - (Solo.headerOffset ? Solo.headerOffset() : 0) - 20;
        window.scrollTo({ top, behavior: 'smooth' });
      }
      this.syncActiveUI();
      const media = $('.s-video', target);
      if (media && opts.userAction && media.dataset.autoplay === 'true') media.play && media.play();
    }
    syncActiveUI() {
      const items = this.items();
      const active = this.activeItem();
      const idx = Math.max(0, items.indexOf(active));
      if (this.dots) $$('.s-dot', this.dots).forEach((d, i) => d.classList.toggle('is-active', i === idx));
      if (this.current) this.current.textContent = idx + 1;
      $$('[data-gallery-prev]', this).forEach((b) => { b.disabled = idx <= 0; });
      $$('[data-gallery-next]', this).forEach((b) => { b.disabled = idx >= items.length - 1; });
      if (this.thumbsList && active) {
        $$('.s-thumbs__item', this.thumbsList).forEach((li) => {
          const btn = $('.s-thumb', li);
          if (li.dataset.target === active.dataset.mediaId) {
            btn.setAttribute('aria-current', 'true');
            this.scrollThumbIntoView(li);
          } else btn.removeAttribute('aria-current');
        });
      }
      if (active) this.style.setProperty('--main-h', active.offsetHeight + 'px');
    }
    onListScroll() {
      if (!this.isSliderMode() || this._programmatic) return;
      cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(() => {
        const items = this.items();
        const left = this.list.scrollLeft + this.list.offsetLeft;
        let best = items[0], bestD = Infinity;
        items.forEach((it) => {
          const d = Math.abs(it.offsetLeft - left - (parseFloat(getComputedStyle(this.list).paddingLeft) || 0));
          if (d < bestD) { bestD = d; best = it; }
        });
        if (best && !best.classList.contains('is-active')) {
          $$('.s-gallery__item', this.list).forEach((el) => el.classList.toggle('is-active', el === best));
          this.pauseOthers(best);
          this.syncActiveUI();
        }
      });
    }
    pauseOthers(active) {
      $$('.s-gallery__item', this.list).forEach((it) => {
        if (it === active) {
          const v = $('s-video', it);
          if (v && v.dataset.autoplay === 'true' && v.video && v.video.paused && !v.video.querySelector('source[data-src]')) v.video.play().catch(() => {});
          return;
        }
        $$('video', it).forEach((v) => v.pause());
        $$('iframe', it).forEach((f) => {
          try {
            f.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
            f.contentWindow.postMessage('{"method":"pause"}', '*');
          } catch (_) { /* ignore */ }
        });
      });
    }
    scrollThumbs(dir) {
      if (!this.thumbsList) return;
      const vertical = getComputedStyle(this.thumbsList).flexDirection === 'column';
      const first = $('.s-thumbs__item', this.thumbsList);
      const size = first ? (vertical ? first.offsetHeight : first.offsetWidth) + 8 : 100;
      this.thumbsList.scrollBy({ left: vertical ? 0 : dir * size * 3, top: vertical ? dir * size * 3 : 0, behavior: 'smooth' });
    }
    scrollThumbIntoView(li) {
      const list = this.thumbsList;
      const vertical = getComputedStyle(list).flexDirection === 'column';
      if (vertical) {
        const top = li.offsetTop - list.offsetTop;
        if (top < list.scrollTop || top + li.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTo({ top: top - list.clientHeight / 2 + li.offsetHeight / 2, behavior: 'smooth' });
      } else {
        const left = li.offsetLeft - list.offsetLeft;
        if (left < list.scrollLeft || left + li.offsetWidth > list.scrollLeft + list.clientWidth) list.scrollTo({ left: left - list.clientWidth / 2 + li.offsetWidth / 2, behavior: 'smooth' });
      }
    }
    updateThumbArrows() {
      if (!this.thumbsList) return;
      const l = this.thumbsList;
      const vertical = getComputedStyle(l).flexDirection === 'column';
      const pos = vertical ? l.scrollTop : l.scrollLeft;
      const max = vertical ? l.scrollHeight - l.clientHeight : l.scrollWidth - l.clientWidth;
      const p = $('[data-thumbs-prev]', this), n = $('[data-thumbs-next]', this);
      if (p) p.disabled = pos <= 2;
      if (n) n.disabled = pos >= max - 2;
    }
    /* variant handling */
    applyVariant(variant, optionValues) {
      const filterOpt = this.dataset.filter;
      if (filterOpt && filterOpt !== 'none') {
        const idx = parseInt(filterOpt.replace('option', ''), 10) - 1;
        const val = optionValues[idx];
        $$('.s-gallery__item, .s-thumbs__item', this).forEach((el) => {
          const alt = el.dataset.alt || '';
          el.classList.toggle('is-filtered', alt !== val && alt !== 'always_display');
        });
        this.buildDots();
        const first = this.items()[0];
        if (variant && variant.featured_media && $(`.s-gallery__item[data-media-id="${variant.featured_media.id}"]:not(.is-filtered)`, this.list)) {
          this.setActive(variant.featured_media.id, { instant: true });
        } else if (first) {
          this.setActive(first.dataset.mediaId, { instant: true });
        }
        return;
      }
      if (this.dataset.hideVariants === 'true') {
        $$('.is-variant-current', this).forEach((el) => el.classList.remove('is-variant-current'));
        if (variant && variant.featured_media) {
          $$(`[data-media-id="${variant.featured_media.id}"], [data-target="${variant.featured_media.id}"]`, this).forEach((el) => el.classList.add('is-variant-current'));
        }
        this.buildDots();
      }
      if (variant && variant.featured_media) this.setActive(variant.featured_media.id, { prepend: true, scrollIntoView: false });
    }
    openLightbox(mediaId) {
      if (!this.lightbox) return;
      Solo.openModal(this.lightbox);
      $$('iframe[data-src]', this.lightbox).forEach((f) => { if (!f.src) f.src = f.dataset.src; });
      const item = $(`.s-lightbox__item[data-media-id="${mediaId}"]`, this.lightbox);
      if (item) setTimeout(() => item.scrollIntoView({ block: 'start' }), 50);
      const v = item && $('s-video', item);
      if (v && v.play) setTimeout(() => v.play(), 200);
    }
  }
  customElements.define('s-gallery', SGallery);
})();

/* ---------------------------------------------------------------- product page */
(function () {
  const { $, $$ } = Solo;

  /* variant helper for any product JSON (main product, upsells, bundles) */
  const Variants = (Solo.Variants = {
    find(variants, options) {
      return variants.find((v) => options.every((o, i) => v.options[i] === o)) || null;
    },
    /* closest available match when the exact combination doesn't exist */
    findClosest(variants, options, changedIndex = 0) {
      let v = this.find(variants, options);
      if (v) return v;
      const val = options[changedIndex];
      return variants.find((x) => x.options[changedIndex] === val && x.available) || variants.find((x) => x.options[changedIndex] === val) || null;
    },
    /* availability state for each option value given the other current selections */
    states(variants, selected, optionIndex) {
      const out = {};
      variants.forEach((v) => {
        const matchesPrev = selected.every((s, i) => i >= optionIndex || v.options[i] === s);
        if (!matchesPrev) return;
        const val = v.options[optionIndex];
        if (!out[val]) out[val] = { exists: true, available: false };
        if (v.available) out[val].available = true;
      });
      return out;
    },
    applyStatesToSelects(container, variants, skipUnavailable) {
      const selects = $$('select[data-option-index]', container);
      const selected = selects.map((s) => s.value);
      selects.forEach((sel) => {
        const idx = parseInt(sel.dataset.optionIndex, 10);
        const st = this.states(variants, selected, idx);
        $$('option', sel).forEach((op) => {
          const s = st[op.value];
          op.classList.toggle('is-missing', !s);
          op.classList.toggle('is-unavailable', !!s && !s.available);
          op.disabled = !s || (skipUnavailable && !s.available);
        });
      });
    },
  });

  /* ===== <s-product> main controller ===== */
  class SProduct extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.sectionId = this.dataset.section;
      this.product = JSON.parse($('[data-product-json]', this).textContent);
      this.formId = this.dataset.form;
      this.form = document.getElementById(this.formId);
      this.picker = $('[data-variant-picker]', this);
      this.gallery = $('s-gallery', this);
      this.breaks = $('[data-qty-breaks]', this);
      this.qtyInput = $('[data-qty-main] input', this);
      this.idInput = this.form ? $('[data-variant-id]', this.form) : null;
      this.atc = $('[data-atc]', this);
      this.sticky = $('s-sticky-atc', this);
      this.variant = this.currentVariantFromInput() || this.product.variants.find((v) => v.available) || this.product.variants[0];

      if (this.picker) {
        this.picker.addEventListener('change', (e) => {
          if (e.target.matches('[data-option-radio], [data-option-select]')) this.onOptionChange(e.target);
        });
        this.refreshOptionStates();
        this.updateLabels();
      }
      if (this.breaks) this.initBreaks();
      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.addToCart({ button: this.atc });
        });
      }
      const checkoutBtn = $('[data-atc-checkout]', this);
      if (checkoutBtn) checkoutBtn.addEventListener('click', () => this.addToCart({ button: checkoutBtn, checkout: true }));
      if (this.qtyInput) this.qtyInput.addEventListener('change', () => this.onQuantityChange());
      this.onQuantityChange();
      Solo.on('cart:updated', (d) => this.updateInCart(d.cart));
      if (this.gallery && this.variant && this.picker) {
        const opts = this.variant.options;
        requestAnimationFrame(() => this.gallery.applyVariant(this.variant, opts));
      }
    }

    currentVariantFromInput() {
      const id = this.idInput ? this.idInput.value : null;
      return this.product.variants.find((v) => String(v.id) === String(id));
    }

    selectedOptions() {
      if (!this.picker) return this.variant ? this.variant.options.slice() : [];
      return $$('.s-opt', this.picker).map((fs) => {
        const type = fs.dataset.type;
        if (type === 'dropdown') return $('select', fs).value;
        const checked = $('input[data-option-radio]:checked', fs);
        return checked ? checked.value : null;
      });
    }

    onOptionChange(input) {
      const fs = input.closest('.s-opt');
      const changedIndex = fs ? parseInt(fs.dataset.optionIndex, 10) : 0;
      const opts = this.selectedOptions();
      let variant = Variants.find(this.product.variants, opts);
      if (!variant || (this.picker.dataset.skipUnavailable === 'true' && !variant.available)) {
        const alt = this.product.variants.find((v) => v.options[changedIndex] === opts[changedIndex] && v.available && v.options.every((o, i) => i <= changedIndex ? o === opts[i] : true))
          || this.product.variants.find((v) => v.options[changedIndex] === opts[changedIndex] && v.available)
          || this.product.variants.find((v) => v.options[changedIndex] === opts[changedIndex]);
        if (alt) {
          variant = alt;
          this.setPickerValues(alt.options);
        }
      }
      this.setVariant(variant, { source: 'picker' });
    }

    setPickerValues(options) {
      if (!this.picker) return;
      $$('.s-opt', this.picker).forEach((fs, i) => {
        const val = options[i];
        if (fs.dataset.type === 'dropdown') {
          $('select', fs).value = val;
        } else {
          $$('input[data-option-radio]', fs).forEach((r) => (r.checked = r.value === val));
        }
      });
    }

    /* public: set variant from any source (picker, sticky bar, url) */
    setVariant(variant, opts = {}) {
      this.variant = variant;
      if (opts.source !== 'picker' && variant) this.setPickerValues(variant.options);
      this.refreshOptionStates();
      this.updateLabels();
      if (!variant) {
        this.setAtcState(false, true);
        return;
      }
      if (this.idInput) this.idInput.value = variant.id;
      $$('input[name="id"]', this).forEach((inp) => { if (inp !== this.idInput && inp.form && inp.form.id !== this.formId) inp.value = variant.id; });
      this.updateUrl(variant);
      this.updatePrices(variant);
      this.setAtcState(variant.available, false);
      if (this.gallery) this.gallery.applyVariant(variant, variant.options);
      this.updateVariantBreaks();
      this.updateQtyBreakPrices();
      this.updateBreakItemSelects(true);
      if (this.sticky && opts.source !== 'sticky') this.sticky.syncVariant(variant);
      this.refreshDynamicParts(variant);
      Solo.emit('variant:change', { section: this.sectionId, variant });
    }

    updateUrl(variant) {
      if (!this.dataset.url || window.Shopify && Shopify.designMode) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    refreshOptionStates() {
      if (!this.picker) return;
      const selected = this.selectedOptions();
      const skip = this.picker.dataset.skipUnavailable === 'true';
      $$('.s-opt', this.picker).forEach((fs) => {
        const idx = parseInt(fs.dataset.optionIndex, 10);
        const st = Variants.states(this.product.variants, selected, idx);
        $$('input[data-option-radio]', fs).forEach((r) => {
          const s = st[r.value];
          r.classList.toggle('is-missing', !s && idx > 0);
          r.classList.toggle('is-unavailable', !!s && !s.available);
          r.disabled = false;
        });
        $$('select option', fs).forEach((op) => {
          const s = st[op.value];
          op.classList.toggle('is-missing', !s && idx > 0);
          op.classList.toggle('is-unavailable', !!s && !s.available);
          op.textContent = op.value + (s && !s.available ? ' - ' + (Solo.strings.unavailable || 'Unavailable') : '');
          op.disabled = skip && !!s && !s.available;
        });
      });
    }

    updateLabels() {
      if (!this.picker) return;
      const sel = this.selectedOptions();
      $$('[data-selected-value]', this.picker).forEach((el) => {
        const fs = el.closest('.s-opt');
        if (fs) el.textContent = sel[parseInt(fs.dataset.optionIndex, 10)] || '';
      });
    }

    priceVals(variant) {
      const p = variant.price, cp = variant.compare_at_price || 0;
      return { price: p, compare: cp, onSale: cp > p };
    }

    updatePrices(variant) {
      const { price, compare, onSale } = this.priceVals(variant);
      [...$$('[data-price-box]', this), ...(this.sticky ? $$('[data-price-box]', this.sticky) : [])].forEach((box) => {
        box.classList.toggle('is-on-sale', onSale);
        box.classList.toggle('is-sold-out', !variant.available);
        const now = $('[data-price-now]', box);
        if (now) now.textContent = Solo.formatMoney(price);
        const was = $('[data-price-was]', box);
        if (was) was.textContent = onSale ? Solo.formatMoney(compare) : '';
        const badge = $('[data-badge-sale]', box);
        if (badge) {
          const pct = onSale ? Math.floor(((compare - price) * 100) / compare) + '%' : '0%';
          const saved = Solo.formatMoneyShort(Math.max(0, compare - price));
          let html = (badge.dataset.template || '').split('[percentage]').join(pct).split('[amount]').join(saved);
          if (badge.dataset.icon1 != null) html = html.split('[icon_1]').join(badge.dataset.icon1).split('[icon_2]').join(badge.dataset.icon2 || '');
          const t = $('[data-badge-text]', badge);
          if (t) t.innerHTML = html;
        }
      });
      const atcPrice = $('[data-atc-price]', this);
      if (atcPrice) {
        atcPrice.textContent = Solo.formatMoney(price * this.currentQuantity()) + ' •';
        atcPrice.hidden = !variant.available;
      }
      $$('[data-sku-value]', this).forEach((el) => (el.textContent = variant.sku || ''));
      const img = this.sticky && $('[data-sticky-img]', this.sticky);
      if (img && variant.featured_image) img.src = variant.featured_image.src + (variant.featured_image.src.includes('?') ? '&' : '?') + 'width=200';
    }

    setAtcState(available, unavailable) {
      const label = unavailable ? Solo.strings.unavailable : available ? null : Solo.strings.soldOut;
      [this.atc, $('[data-atc-checkout]', this)].forEach((btn) => {
        if (!btn) return;
        btn.disabled = !available;
        const t = $('[data-atc-text]', btn);
        if (t) t.textContent = label || t.dataset.label;
      });
      if (this.sticky) this.sticky.setAvailable(available, label);
    }

    /* fetch the section re-rendered for this variant and swap server-only parts */
    async refreshDynamicParts(variant) {
      const needs = $$('[data-inventory], .s-pickup, [data-qty-rules], .s-installments', this);
      if (!needs.length) return;
      const reqId = (this._req = (this._req || 0) + 1);
      try {
        const doc = await Solo.fetchHTML(`${this.dataset.url}?variant=${variant.id}&section_id=${this.sectionId}`);
        if (reqId !== this._req) return;
        ['[data-inventory]', '[data-qty-rules]', '.s-installments'].forEach((sel) => {
          const fresh = $(sel, doc), cur = $(sel, this);
          if (fresh && cur) cur.innerHTML = fresh.innerHTML;
        });
        const freshP = $('.s-pickup', doc), curP = $('.s-pickup', this);
        if (curP && !freshP) curP.remove();
        else if (freshP && curP) curP.innerHTML = freshP.innerHTML;
        else if (freshP && !curP && this.form) this.form.insertAdjacentElement('afterend', freshP);
        const freshQty = $('[data-qty-main] input', doc);
        if (freshQty && this.qtyInput) {
          ['min', 'max', 'step'].forEach((a) => {
            if (freshQty.hasAttribute(a)) this.qtyInput.setAttribute(a, freshQty.getAttribute(a));
            else this.qtyInput.removeAttribute(a);
          });
        }
      } catch (_) { /* ignore */ }
    }

    /* ---------- quantity & breaks ---------- */
    currentQuantity() {
      if (this.breaks) {
        const r = $('input[data-break-input]:checked', this.breaks);
        return r ? parseInt(r.value, 10) || 1 : 1;
      }
      if (this.qtyInput) return Math.max(1, parseInt(this.qtyInput.value, 10) || 1);
      return 1;
    }

    onQuantityChange() {
      const q = this.currentQuantity();
      const gifts = $('s-gifts', this);
      if (gifts) gifts.update(q);
      if (this.variant) {
        const atcPrice = $('[data-atc-price]', this);
        if (atcPrice) atcPrice.textContent = Solo.formatMoney(this.variant.price * q) + ' •';
      }
    }

    initBreaks() {
      this.breaks.addEventListener('change', (e) => {
        if (e.target.matches('input[data-break-input]')) {
          this.onQuantityChange();
          return;
        }
        if (e.target.matches('.s-break__item select')) this.updateBreakItemSelects(false);
      });
      this.breaks.addEventListener('click', (e) => {
        if (e.target.closest('.s-break__variants')) e.preventDefault();
      });
      this.updateBreakItemSelects(false);
    }

    updateBreakItemSelects(syncToVariant) {
      if (!this.breaks) return;
      const skip = this.breaks.dataset.skipUnavailable === 'true';
      $$('[data-break-item]', this.breaks).forEach((item) => {
        if (syncToVariant && this.variant) $$('select', item).forEach((s, i) => (s.value = this.variant.options[i]));
        Variants.applyStatesToSelects(item, this.product.variants, skip);
      });
    }

    updateQtyBreakPrices() {
      if (!this.breaks || this.breaks.dataset.updatePrices !== 'true' || !this.variant) return;
      const v = this.variant;
      $$('[data-break]', this.breaks).forEach((card) => {
        const q = parseInt(card.dataset.qty, 10) || 1;
        const pctLeft = parseFloat(card.dataset.pctLeft) || 1;
        const fixed = parseFloat(card.dataset.fixed) || 0;
        const price = Math.round(v.price * q * pctLeft - fixed);
        const unitCmp = card.dataset.cmpMode === 'compare_price' && v.compare_at_price > v.price ? v.compare_at_price : v.price;
        const cmp = unitCmp * q;
        const saved = cmp - price;
        const vals = { quantity: q, price, compare_price: cmp, amount_saved: saved, amount_saved_rounded: Math.round(saved / 100) * 100, price_each: price / q, compare_price_each: cmp / q };
        $$('[data-text]', card).forEach((el) => (el.innerHTML = Solo.moneyText(el.dataset.text, vals)));
        const c = $('.s-break__compare', card);
        if (c) c.classList.toggle('is-hidden', !(cmp > price));
      });
    }

    updateVariantBreaks() {
      if (!this.picker) return;
      const sel = this.selectedOptions();
      $$('.s-opt--breaks', this.picker).forEach((fs) => {
        const idx = parseInt(fs.dataset.optionIndex, 10);
        $$('[data-variant-break]', fs).forEach((card) => {
          const opts = sel.slice();
          opts[idx] = card.dataset.variantBreak;
          const v = Variants.find(this.product.variants, opts) || this.product.variants.find((x) => x.options[idx] === card.dataset.variantBreak);
          if (!v) return;
          const cmp = v.compare_at_price || 0;
          const saved = Math.max(0, cmp - v.price);
          const vals = { name: card.dataset.variantBreak, price: v.price, compare_price: cmp, amount_saved: saved, amount_saved_rounded: Math.round(saved / 100) * 100 };
          $$('[data-text]', card).forEach((el) => (el.innerHTML = Solo.moneyText(el.dataset.text, vals)));
          const c = $('.s-break__compare', card);
          if (c) c.classList.toggle('is-hidden', !(cmp > v.price));
        });
      });
    }

    updateInCart(cartData) {
      const label = $('[data-in-cart]', this);
      if (!label || !cartData || !this.variant) return;
      const q = cartData.items.filter((i) => i.variant_id === this.variant.id).reduce((s, i) => s + i.quantity, 0);
      label.hidden = !q;
      label.textContent = ` (${q} ${Solo.strings.inCart || 'in cart'})`;
    }

    /* ---------- validation ---------- */
    validateFields(fromSticky) {
      let firstInvalid = null;
      let message = '';
      $$('s-field', this).forEach((f) => {
        if (f.dataset.required !== 'true') return;
        const inputs = $$('[data-field-input]', f);
        const ok = inputs.some((i) => (i.type === 'radio' ? i.checked : String(i.value || '').trim() !== ''));
        f.classList.toggle('is-invalid', !ok);
        if (!ok && !firstInvalid) {
          firstInvalid = f;
          message = f.dataset.atcError || '';
        }
      });
      if (firstInvalid) {
        this.showError(message);
        const top = firstInvalid.getBoundingClientRect().top + window.scrollY - 120;
        window.scrollTo({ top, behavior: 'smooth' });
        const sticky = this.sticky;
        if (fromSticky && sticky && firstInvalid.dataset.stickyError === 'true') sticky.flashError(message);
        return false;
      }
      return true;
    }

    properties() {
      const props = {};
      if (!this.form) return props;
      const fd = new FormData(this.form);
      for (const [k, v] of fd.entries()) {
        const m = k.match(/^properties\[(.+)\]$/);
        if (m && String(v).trim() !== '') props[m[1]] = v;
      }
      return props;
    }

    mainItems() {
      const props = this.properties();
      const hasProps = Object.keys(props).length > 0;
      const withProps = (o) => (hasProps ? { ...o, properties: props } : o);
      if (this.breaks) {
        const r = $('input[data-break-input]:checked', this.breaks);
        if (!r) return [];
        const q = parseInt(r.value, 10) || 1;
        const card = r.nextElementSibling;
        const selItems = card ? $$('[data-break-item]', card) : [];
        if (selItems.length) {
          const counts = {};
          selItems.forEach((it) => {
            const opts = $$('select', it).map((s) => s.value);
            const v = Variants.find(this.product.variants, opts) || this.variant;
            counts[v.id] = (counts[v.id] || 0) + 1;
          });
          return Object.keys(counts).map((id) => withProps({ id: Number(id), quantity: counts[id] }));
        }
        return [withProps({ id: this.variant.id, quantity: q })];
      }
      return [withProps({ id: this.variant.id, quantity: this.currentQuantity() })];
    }

    extraItems(includeUpsells = true) {
      const items = [];
      if (includeUpsells) {
        $$('s-upsell[data-mode="toggle"][data-selected="true"]', this).forEach((u) => { if (!u.dataset.cartUpsell && !u.closest('s-bundle')) items.push({ id: Number(u.dataset.variant), quantity: 1 }); });
      }
      $$('s-bundle[data-mode="main"] s-upsell', this).forEach((u) => items.push({ id: Number(u.dataset.variant), quantity: 1 }));
      const gifts = $('s-gifts', this);
      if (gifts) gifts.unlocked(this.currentQuantity()).forEach((id) => items.push({ id: Number(id), quantity: 1 }));
      return items;
    }

    showError(msg) {
      const box = $('[data-form-error]', this);
      if (!box) { if (msg) Solo.toast(msg); return; }
      box.hidden = !msg;
      const t = $('[data-form-error-text]', box);
      if (t) t.textContent = msg || '';
    }

    async addToCart({ button, checkout = false, fromSticky = false } = {}) {
      if (!this.variant || !this.variant.available) return;
      this.showError('');
      if (!this.validateFields(fromSticky)) return;
      const items = [...this.mainItems(), ...this.extraItems()];
      const merged = [];
      items.forEach((it) => {
        const ex = merged.find((m) => m.id === it.id && !m.properties && !it.properties);
        if (ex) ex.quantity += it.quantity;
        else merged.push({ ...it });
      });
      const skip = checkout || (this.atc && this.atc.dataset.skipCart === 'true');
      const btns = [button, fromSticky ? null : null].filter(Boolean);
      btns.forEach((b) => b.classList.add('is-loading'));
      try {
        await Solo.cart.add(merged, { open: !skip });
        if (skip) window.location.href = '/checkout';
      } catch (e) {
        this.showError(e.message);
        if (fromSticky && this.sticky) this.sticky.flashError(e.message);
      }
      btns.forEach((b) => b.classList.remove('is-loading'));
    }
  }
  customElements.define('s-product', SProduct);

  /* ===== <s-upsell> one upsell / bundle row ===== */
  class SUpsell extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      const json = $('[data-upsell-json]', this);
      this.variants = json ? JSON.parse(json.textContent) : null;
      const sel = $('[data-upsell-variants]', this);
      if (sel && this.variants) {
        Variants.applyStatesToSelects(sel, this.variants, this.dataset.skipUnavailable === 'true');
        sel.addEventListener('change', (e) => {
          e.stopPropagation();
          const opts = $$('select', sel).map((s) => s.value);
          const changed = parseInt(e.target.dataset.optionIndex, 10) || 0;
          let v = Variants.findClosest(this.variants, opts, changed);
          if (v && !Variants.find(this.variants, opts)) $$('select', sel).forEach((s, i) => (s.value = v.options[i]));
          Variants.applyStatesToSelects(sel, this.variants, this.dataset.skipUnavailable === 'true');
          if (v) this.setVariant(v);
        });
        sel.addEventListener('click', (e) => e.stopPropagation());
      }
      $$('[data-upsell-toggle]', this).forEach((t) => t.addEventListener('click', (e) => {
        if (e.target.closest('[data-upsell-variants], a')) return;
        e.preventDefault();
        this.toggle();
      }));
      const add = $('[data-upsell-add]', this);
      if (add) add.addEventListener('click', async (e) => {
        e.preventDefault();
        add.classList.add('is-loading');
        try { await Solo.cart.add([{ id: Number(this.dataset.variant), quantity: 1 }], { open: !this.dataset.cartUpsell }); } catch (err) { Solo.toast(err.message); }
        add.classList.remove('is-loading');
      });
      if (this.dataset.cartUpsell !== undefined && this.dataset.mode === 'toggle') this.syncWithCart();
    }
    syncWithCart() {
      const data = Solo.cart.data;
      if (!data) return;
      const inCart = data.items.some((i) => i.handle === this.dataset.handle);
      this.dataset.selected = inCart ? 'true' : 'false';
    }
    async toggle() {
      const next = this.dataset.selected !== 'true';
      if (this.dataset.cartUpsell !== undefined) {
        if (this._busy) return;
        this._busy = true;
        try {
          if (next) await Solo.cart.add([{ id: Number(this.dataset.variant), quantity: 1 }], { open: false });
          else {
            const line = (Solo.cart.data.items || []).find((i) => i.handle === this.dataset.handle);
            if (line) await Solo.cart.change(line.key, 0);
          }
        } catch (err) { Solo.toast(err.message); }
        this._busy = false;
        return;
      }
      this.dataset.selected = next ? 'true' : 'false';
      Solo.emit('upsell:toggle', { el: this });
    }
    setVariant(v) {
      this.dataset.variant = v.id;
      const img = $('[data-upsell-img]', this);
      if (img && v.featured_image) img.src = v.featured_image.src + (v.featured_image.src.includes('?') ? '&' : '?') + 'width=300';
      if (this.dataset.updatePrices === 'true') {
        const pl = parseFloat(this.dataset.pctLeft) || 1;
        const fx = parseFloat(this.dataset.fixed) || 0;
        const price = Math.max(0, Math.round(v.price * pl - fx));
        const cmp = v.compare_at_price > v.price ? v.compare_at_price : v.price;
        this.dataset.price = price;
        this.dataset.compare = cmp;
        $$('[data-up-price]', this).forEach((el) => (el.textContent = price === 0 && this.dataset.freeText ? this.dataset.freeText : Solo.formatMoney(price)));
        $$('[data-up-compare]', this).forEach((el) => (el.textContent = cmp > price ? Solo.formatMoney(cmp) : ''));
        const bundle = this.closest('s-bundle');
        if (bundle) bundle.recalc();
      }
      if (this.dataset.cartUpsell !== undefined && this.dataset.selected === 'true') {
        /* swap the variant in cart */
        const line = (Solo.cart.data.items || []).find((i) => i.handle === this.dataset.handle);
        if (line && line.variant_id !== v.id) Solo.cart.change(line.key, 0).then(() => Solo.cart.add([{ id: v.id, quantity: 1 }], { open: false }));
      }
    }
  }
  customElements.define('s-upsell', SUpsell);
  Solo.on('content:loaded', ({ root }) => {
    $$('s-upsell', root).forEach((u) => { if (u.dataset.cartUpsell !== undefined && u.dataset.mode === 'toggle') u.syncWithCart(); });
  });
  Solo.on('cart:updated', () => $$('s-upsell[data-cart-upsell][data-mode="toggle"]').forEach((u) => u.syncWithCart()));

  /* ===== <s-bundle> ===== */
  class SBundle extends HTMLElement {
    connectedCallback() {
      const btn = $('[data-bundle-add]', this);
      if (btn) btn.addEventListener('click', () => this.add(btn));
    }
    recalc() {
      const ups = $$('s-upsell', this);
      if (!ups.some((u) => u.dataset.price)) return;
      let total = 0, cmp = 0;
      ups.forEach((u) => {
        total += parseFloat(u.dataset.price || 0);
        cmp += parseFloat(u.dataset.compare || u.dataset.price || 0);
      });
      total = Math.max(0, Math.round(total * (parseFloat(this.dataset.totalPctLeft) || 1) - (parseFloat(this.dataset.totalFixed) || 0)));
      const t = $('[data-bundle-total]', this), c = $('[data-bundle-total-cmp]', this);
      if (t) t.textContent = Solo.formatMoney(total);
      if (c) c.textContent = cmp > total ? Solo.formatMoney(cmp) : '';
    }
    async add(btn) {
      const items = $$('s-upsell', this).map((u) => ({ id: Number(u.dataset.variant), quantity: 1 }));
      if (this.dataset.includeUpsells === 'true') {
        const prod = this.closest('s-product');
        if (prod) $$('s-upsell[data-mode="toggle"][data-selected="true"]', prod).forEach((u) => { if (!u.closest('s-bundle')) items.push({ id: Number(u.dataset.variant), quantity: 1 }); });
      }
      const err = $('[data-bundle-error]', this);
      btn.classList.add('is-loading');
      try {
        await Solo.cart.add(items);
        if (err) err.hidden = true;
      } catch (e) {
        if (err) { err.textContent = e.message; err.hidden = false; }
      }
      btn.classList.remove('is-loading');
    }
  }
  customElements.define('s-bundle', SBundle);

  /* ===== <s-gifts> quantity based gifts ===== */
  class SGifts extends HTMLElement {
    connectedCallback() {
      this.gifts = $$('[data-gift]', this);
      const product = this.closest('s-product');
      if (product && product.product) {
        Solo.giftRules = Solo.giftRules || [];
        Solo.giftRules.push({ mainHandle: product.product.handle, gifts: this.gifts.map((g) => ({ variant: g.dataset.variant, qty: parseInt(g.dataset.qty, 10) || 1 })) });
      }
    }
    update(q) {
      (this.gifts || $$('[data-gift]', this)).forEach((g) => (g.dataset.unlocked = q >= (parseInt(g.dataset.qty, 10) || 1) ? 'true' : 'false'));
    }
    unlocked(q) {
      const inCart = Solo.cart.data ? Solo.cart.data.items.map((i) => String(i.variant_id)) : [];
      return (this.gifts || []).filter((g) => q >= (parseInt(g.dataset.qty, 10) || 1) && !inCart.includes(String(g.dataset.variant))).map((g) => g.dataset.variant);
    }
  }
  customElements.define('s-gifts', SGifts);

  /* custom fields: clear errors while typing */
  class SField extends HTMLElement {
    connectedCallback() {
      this.addEventListener('input', () => this.classList.remove('is-invalid'));
      this.addEventListener('change', () => this.classList.remove('is-invalid'));
    }
  }
  customElements.define('s-field', SField);
  customElements.define('s-breaks', class extends HTMLElement {});

  /* ===== <s-sticky-atc> ===== */
  class SStickyAtc extends HTMLElement {
    connectedCallback() {
      this.product = this.closest('s-product');
      this.btn = $('[data-sticky-btn]', this);
      this.text = $('[data-sticky-text]', this);
      if (document.body !== this.parentElement) document.body.appendChild(this);
      this.btn.addEventListener('click', () => this.onClick());
      const combined = $('[data-sticky-combined]', this);
      if (combined) combined.addEventListener('change', () => {
        const v = this.product.product.variants.find((x) => String(x.id) === combined.value);
        if (v) this.product.setVariant(v, { source: 'sticky' });
      });
      $$('[data-sticky-option]', this).forEach((s) => s.addEventListener('change', () => {
        const opts = $$('[data-sticky-option]', this).map((x) => x.value);
        const v = Variants.findClosest(this.product.product.variants, opts, parseInt(s.dataset.stickyOption, 10));
        if (v) this.product.setVariant(v, { source: 'sticky' });
      }));
      if (this.dataset.when === 'after_scroll') this.observe();
      else this.setVisible(true);
    }
    observe() {
      const target = this.product && ($('[data-atc]', this.product) || $('.s-product__info', this.product));
      if (!target) { this.setVisible(true); return; }
      const io = new IntersectionObserver((ents) => {
        ents.forEach((en) => this.setVisible(!en.isIntersecting && en.boundingClientRect.top < 0));
      });
      io.observe(target);
    }
    setVisible(v) {
      this.classList.toggle('is-visible', v);
      document.documentElement.style.setProperty('--sticky-h', v ? this.offsetHeight + 'px' : '0px');
      document.documentElement.classList.toggle('has-sticky-atc', v);
    }
    onClick() {
      const fn = this.dataset.function;
      if (fn === 'add_to_cart') {
        if (this.product) this.product.addToCart({ button: this.btn, fromSticky: true });
        return;
      }
      const selector = fn.replace('scroll_', '').replace('-id', '-' + (this.product ? this.product.dataset.section : ''));
      let target = null;
      if (selector === '.pricing-table') target = $('.s-pricing-table') || $('.pricing-table');
      else if (selector.startsWith('#ProductInfo')) target = this.product && $('.s-product__info', this.product);
      else if (selector.startsWith('#variant-selects')) target = this.product && $('[data-variant-picker]', this.product);
      else if (selector.startsWith('#quantity-breaks')) target = this.product && $('[data-qty-breaks]', this.product);
      if (target) window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - 100, behavior: 'smooth' });
    }
    syncVariant(v) {
      const combined = $('[data-sticky-combined]', this);
      if (combined) combined.value = v.id;
      $$('[data-sticky-option]', this).forEach((s, i) => (s.value = v.options[i]));
      const price = $('[data-sticky-price]', this);
      if (price) price.textContent = Solo.formatMoney(v.price) + ' •';
    }
    setAvailable(available, label) {
      if (this.dataset.function !== 'add_to_cart') return;
      this.btn.disabled = !available;
      if (this.text) this.text.textContent = label || this.text.dataset.label;
    }
    flashError(msg) {
      if (!msg || !this.text) return;
      const orig = this.text.dataset.label;
      this.text.textContent = msg;
      setTimeout(() => (this.text.textContent = orig), 2500);
    }
  }
  customElements.define('s-sticky-atc', SStickyAtc);

  /* quick add buttons (complementary products, section ATC with a custom product) */
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-quick-add]');
    if (!b) return;
    e.preventDefault();
    const skip = b.dataset.skipCart === 'true';
    b.classList.add('is-loading');
    try {
      await Solo.cart.add([{ id: Number(b.dataset.quickAdd), quantity: 1 }], { open: !skip });
      if (skip) window.location.href = '/checkout';
    } catch (err) { Solo.toast(err.message); }
    b.classList.remove('is-loading');
  });

  /* section ATC buttons that submit the main product form */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-main-atc]');
    if (!b) return;
    e.preventDefault();
    const main = document.querySelector('s-product[data-main-product]');
    if (main) main.addToCart({ button: b });
  });
})();

/* ---------------------------------------------------------------- header, menu, search, popups, floating buttons */
(function () {
  const { $, $$ } = Solo;

  /* ===== <s-header> : sticky behaviour ===== */
  class SHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this.parentElement;
      this.mode = this.dataset.sticky || 'none';
      this.section.classList.add('s-hdr-section');
      if (this.mode === 'none') {
        this.section.classList.add('is-static');
        return;
      }
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.setHeight();
      window.addEventListener('resize', Solo.debounce(() => this.setHeight(), 150));
      Solo.headerOffset = () => {
        if (this.mode === 'none') return 0;
        if (this.mode === 'on-scroll-up' && this.section.classList.contains('is-hidden')) return 0;
        return this.offsetHeight;
      };
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }
    setHeight() {
      document.documentElement.style.setProperty('--header-height', this.offsetHeight + 'px');
    }
    naturalBottom() {
      // a sticky element reports its stuck position, so measure from the sections before it
      let top = 0;
      for (let p = this.section.previousElementSibling; p; p = p.previousElementSibling) {
        if (p.classList && p.classList.contains('shopify-section')) top += p.offsetHeight;
      }
      return top + this.offsetHeight;
    }
    onScroll() {
      const y = window.scrollY;
      const bottom = this.naturalBottom();
      document.documentElement.classList.toggle('is-scrolled-past', y > bottom);
      if (this.mode === 'on-scroll-up') {
        if (this.lock) { this.lastY = y; return; }
        if (y > this.lastY && y > bottom) {
          this.section.classList.add('is-hidden');
          $$('s-dropdown', this).forEach((d) => d.close && d.close());
        } else if (y < this.lastY) {
          this.section.classList.remove('is-hidden');
        }
        if (y <= 0) this.section.classList.remove('is-hidden');
      }
      this.lastY = y;
    }
  }
  customElements.define('s-header', SHeader);

  /* ===== desktop dropdowns / mega menus ===== */
  class SDropdown extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector(':scope > button');
      this.panel = this.btn && this.btn.nextElementSibling;
      if (!this.btn || !this.panel) return;
      this.btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.isOpen() ? this.close() : this.open();
      });
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.close();
      });
      this.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') { this.close(); this.btn.focus(); }
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.close();
      });
    }
    isOpen() { return this.btn.getAttribute('aria-expanded') === 'true'; }
    open() {
      const parent = this.parentElement.closest('s-dropdown');
      $$('s-dropdown').forEach((d) => { if (d !== this && d !== parent && !d.contains(this) && d.close) d.close(); });
      this.btn.setAttribute('aria-expanded', 'true');
      this.panel.hidden = false;
    }
    close() {
      if (!this.btn) return;
      this.btn.setAttribute('aria-expanded', 'false');
      this.panel.hidden = true;
    }
  }
  customElements.define('s-dropdown', SDropdown);

  /* ===== mobile menu drawer ===== */
  class SMenuDrawer extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-drawer-open]');
      this.drawer = this.querySelector('.s-mdrawer');
      if (!this.toggle || !this.drawer) return;
      this.toggle.addEventListener('click', () => this.open());
      this.drawer.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) return this.close();
        const sub = e.target.closest('[data-sub-open]');
        if (sub) {
          const panel = document.getElementById(sub.dataset.subOpen);
          if (panel) {
            panel.classList.add('is-open');
            sub.setAttribute('aria-expanded', 'true');
            const first = panel.querySelector('[data-sub-back]');
            if (first) setTimeout(() => first.focus(), 200);
          }
          return;
        }
        const back = e.target.closest('[data-sub-back]');
        if (back) {
          const panel = back.closest('[data-sub]');
          panel.classList.remove('is-open');
          const opener = this.drawer.querySelector(`[data-sub-open="${panel.id}"]`);
          if (opener) { opener.setAttribute('aria-expanded', 'false'); opener.focus(); }
        }
      });
      Solo.on('escape', () => this.close());
      window.matchMedia('(min-width: 990px)').addEventListener('change', (m) => { if (m.matches) this.close(); });
    }
    open() {
      this.drawer.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => this.drawer.classList.add('is-open')));
      this.toggle.setAttribute('aria-expanded', 'true');
      Solo.lockScroll && Solo.lockScroll();
      const panel = $('.s-mdrawer__panel', this.drawer);
      if (panel) setTimeout(() => panel.focus(), 50);
    }
    close() {
      if (!this.drawer || this.drawer.hidden) return;
      this.drawer.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      setTimeout(() => {
        this.drawer.hidden = true;
        $$('[data-sub].is-open', this.drawer).forEach((p) => p.classList.remove('is-open'));
      }, 220);
      Solo.unlockScroll && Solo.unlockScroll();
    }
  }
  customElements.define('s-menu-drawer', SMenuDrawer);

  /* ===== search dialog with predictive results ===== */
  class SSearch extends HTMLElement {
    connectedCallback() {
      this.openBtn = this.querySelector('[data-search-open]');
      this.modal = this.querySelector('.s-search__modal');
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      this.reset = this.querySelector('button[type="reset"]');
      if (!this.openBtn || !this.modal) return;
      this.openBtn.addEventListener('click', () => this.open());
      this.modal.addEventListener('click', (e) => { if (e.target.closest('[data-search-close]')) this.close(); });
      this.input.addEventListener('input', Solo.debounce(() => this.onInput(), 250));
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      if (this.reset) this.reset.addEventListener('click', (e) => {
        e.preventDefault();
        this.input.value = '';
        this.onInput();
        this.input.focus();
      });
      Solo.on('escape', () => this.close());
    }
    open() {
      const header = this.closest('s-header');
      if (header) header.lock = true;
      this.modal.hidden = false;
      this.openBtn.setAttribute('aria-expanded', 'true');
      document.body.classList.add('s-search-open');
      setTimeout(() => { this.input.focus(); const v = this.input.value; this.input.value = ''; this.input.value = v; }, 30);
    }
    close() {
      if (this.modal.hidden) return;
      this.modal.hidden = true;
      this.openBtn.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('s-search-open');
      const header = this.closest('s-header');
      if (header) header.lock = false;
      this.hideResults();
    }
    hideResults() {
      if (!this.results) return;
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }
    onKey(e) {
      if (!this.results || this.results.hidden) return;
      const items = $$('[role="option"]', this.results);
      if (!items.length) return;
      const idx = items.findIndex((i) => i.getAttribute('aria-selected') === 'true');
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        let n = e.key === 'ArrowDown' ? idx + 1 : idx - 1;
        if (n < 0) n = items.length - 1;
        if (n >= items.length) n = 0;
        items.forEach((i, k) => i.setAttribute('aria-selected', k === n ? 'true' : 'false'));
        items[n].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && idx > -1) {
        e.preventDefault();
        const a = items[idx].querySelector('a') || items[idx];
        if (a.href) window.location.href = a.href; else a.click();
      }
    }
    async onInput() {
      const q = this.input.value.trim();
      if (this.reset) this.reset.hidden = !q;
      if (!q || this.dataset.predictive !== 'true' || !this.results) return this.hideResults();
      const ctrl = (this._ctrl && this._ctrl.abort(), (this._ctrl = new AbortController()));
      try {
        const url = `${this.dataset.url}.json?q=${encodeURIComponent(q)}&resources[type]=query,product,collection,page,article&resources[limit]=4&resources[options][unavailable_products]=last`;
        const res = await fetch(url, { signal: ctrl.signal });
        if (!res.ok) throw new Error('search');
        const data = await res.json();
        this.render(q, data.resources && data.resources.results ? data.resources.results : {});
      } catch (err) {
        if (err.name !== 'AbortError') this.hideResults();
      }
    }
    render(q, r) {
      const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      const S = Solo.strings || {};
      let html = '';
      const queries = r.queries || [];
      const products = r.products || [];
      const others = [].concat(r.collections || [], r.pages || [], r.articles || []);
      if (queries.length) {
        html += `<p class="s-sr__head">${esc(S.suggestions || 'Suggestions')}</p><ul class="s-sr__list" role="listbox">`;
        html += queries.map((s) => `<li role="option" aria-selected="false"><a class="s-sr__item" href="${s.url}"><span class="s-sr__title">${s.styled_text || esc(s.text)}</span></a></li>`).join('');
        html += '</ul>';
      }
      if (products.length) {
        html += `<p class="s-sr__head">${esc(S.products || 'Products')}</p><ul class="s-sr__list" role="listbox">`;
        html += products.map((p) => {
          const img = p.featured_image && p.featured_image.url ? `<img src="${p.featured_image.url}${p.featured_image.url.includes('?') ? '&' : '?'}width=150" alt="" width="50" height="50" loading="lazy">` : '';
          const vendor = this.dataset.vendor === 'true' && p.vendor ? `<span class="s-sr__meta">${esc(p.vendor)}</span>` : '';
          let price = '';
          if (this.dataset.price === 'true' && p.price != null) {
            const cents = Math.round(parseFloat(p.price) * 100);
            price = `<span class="s-sr__meta">${Solo.formatMoney ? Solo.formatMoney(cents) : esc(p.price)}</span>`;
          }
          return `<li role="option" aria-selected="false"><a class="s-sr__item" href="${p.url}">${img}<span class="s-sr__info"><span class="s-sr__title">${esc(p.title)}</span>${vendor}${price}</span></a></li>`;
        }).join('');
        html += '</ul>';
      }
      if (others.length) {
        html += `<p class="s-sr__head">${esc(S.pages || 'Pages')}</p><ul class="s-sr__list" role="listbox">`;
        html += others.map((o) => `<li role="option" aria-selected="false"><a class="s-sr__item" href="${o.url}"><span class="s-sr__title">${esc(o.title)}</span></a></li>`).join('');
        html += '</ul>';
      }
      if (!html) html = `<p class="s-sr__empty">${esc((S.noResults || 'No results found for “[terms]”').replace('[terms]', q))}</p>`;
      html += `<button type="submit" class="s-sr__all"><span>${esc((S.searchFor || 'Search for “[terms]”').replace('[terms]', q))}</span>${'→'}</button>`;
      this.results.innerHTML = html;
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }
  }
  customElements.define('s-search', SSearch);

  /* ===== copy code button (announcement discount) ===== */
  class SCopyCode extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', async () => {
        try { await navigator.clipboard.writeText(this.dataset.code || ''); } catch (_) { /* ignore */ }
        this.classList.add('is-copied');
        clearTimeout(this._t);
        this._t = setTimeout(() => this.classList.remove('is-copied'), 2000);
      });
    }
  }
  customElements.define('s-copy-code', SCopyCode);

  /* ===== country / language selector ===== */
  class SLocale extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('.s-loc__btn');
      this.list = this.querySelector('.s-loc__list');
      this.input = this.querySelector('input[type="hidden"]');
      if (!this.btn) return;
      this.btn.addEventListener('click', () => {
        const open = this.list.hidden;
        this.list.hidden = !open;
        this.btn.setAttribute('aria-expanded', String(open));
      });
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.hide(); });
      this.addEventListener('keyup', (e) => { if (e.key === 'Escape') { this.hide(); this.btn.focus(); } });
      $$('a[data-value]', this).forEach((a) => a.addEventListener('click', (e) => {
        e.preventDefault();
        this.input.value = a.dataset.value;
        const form = this.closest('form');
        if (form) form.submit();
      }));
    }
    hide() { this.list.hidden = true; this.btn.setAttribute('aria-expanded', 'false'); }
  }
  customElements.define('s-locale', SLocale);

  /* ===== promo popup ===== */
  class SPromo extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-promo-main]');
      this.success = this.querySelector('[data-promo-success]');
      this.key = 'solo-promo-' + location.host;
      this.subKey = 'solo-promo-sub-' + location.host;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-promo-close]')) this.hide(e.target.closest('.s-popup'));
        const copy = e.target.closest('[data-copy-text]');
        if (copy) {
          navigator.clipboard && navigator.clipboard.writeText(copy.dataset.copyText).catch(() => {});
          const msg = this.querySelector('.s-promo__copied');
          if (msg) msg.hidden = false;
        }
      });
      const posted = this.querySelector('[data-promo-posted]');
      let subscribed = false;
      try { subscribed = !!localStorage.getItem(this.subKey); } catch (_) { /* ignore */ }
      if (this.dataset.successTest === 'true' || (posted && !subscribed)) {
        try { localStorage.setItem(this.subKey, '1'); } catch (_) { /* ignore */ }
        return this.show(this.success);
      }
      if (this.dataset.test === 'true') return this.show(this.main);
      if (subscribed) return;
      let last = 0;
      try { last = parseInt(localStorage.getItem(this.key) || '0', 10); } catch (_) { /* ignore */ }
      const days = parseFloat(this.dataset.days || '0');
      if (last && Date.now() - last < days * 864e5) return;
      setTimeout(() => this.show(this.main), parseFloat(this.dataset.seconds || '0') * 1000);
    }
    show(el) {
      if (!el) return;
      el.hidden = false;
      if (el === this.main) {
        try { localStorage.setItem(this.key, String(Date.now())); } catch (_) { /* ignore */ }
        this.startTimer();
      }
    }
    hide(el) {
      if (el) el.hidden = true;
      clearInterval(this._timer);
    }
    startTimer() {
      const box = this.querySelector('[data-promo-timer]');
      const mins = parseFloat(this.dataset.timer || '0');
      if (!box || !mins) return;
      const end = Date.now() + mins * 60000;
      const m = $('[data-m]', box), s = $('[data-s]', box);
      const tick = () => {
        const left = Math.max(0, end - Date.now());
        m.textContent = String(Math.floor(left / 60000)).padStart(2, '0');
        s.textContent = String(Math.floor((left % 60000) / 1000)).padStart(2, '0');
        if (!left) clearInterval(this._timer);
      };
      tick();
      this._timer = setInterval(tick, 1000);
    }
  }
  customElements.define('s-promo', SPromo);

  /* ===== music player ===== */
  class SMusic extends HTMLElement {
    connectedCallback() {
      this.audio = this.querySelector('audio');
      this.btn = this.querySelector('button');
      if (!this.audio || !this.btn) return;
      this.audio.volume = Math.min(1, Math.max(0, parseFloat(this.dataset.volume || '0.1')));
      this.btn.addEventListener('click', () => {
        if (this.audio.paused) {
          this.audio.play().then(() => this.btn.setAttribute('aria-pressed', 'true')).catch(() => {});
        } else {
          this.audio.pause();
          this.btn.setAttribute('aria-pressed', 'false');
        }
      });
    }
  }
  customElements.define('s-music', SMusic);

  /* ===== scroll-to-top button ===== */
  document.addEventListener('DOMContentLoaded', () => {
    $$('.s-totop').forEach((btn) => {
      const after = parseInt(btn.dataset.after || '0', 10);
      btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
      if (after > 0) {
        const check = () => { btn.hidden = window.scrollY <= after; };
        window.addEventListener('scroll', check, { passive: true });
        check();
      }
    });
  });
})();
